import ImageBox from "@components/ImageBox/ImageBox";
import { ATTACHMENT_MAX_SIZE_MB, BYTES_IN_MB } from "@constants";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ErrorIcon from "@mui/icons-material/Error";
import PhotoIcon from "@mui/icons-material/InsertPhotoOutlined";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  UploadPayload,
  useCommentsApiV1CreateAttachmentMutation,
} from "@providers/hop-ord-server/api";
import { uploadToS3 } from "@utils/s3";
import React, { useEffect, useRef, useState } from "react";

interface ISendComment {
  isLoading: boolean;
  sendComment: (comment: string, fileIDs: number[]) => void;
  orderGroupId: number;
}

const SendComment = ({
  isLoading,
  sendComment,
  orderGroupId,
}: ISendComment): JSX.Element => {
  const [comment, setComment] = useState("");
  const [storedFiles, setStoredFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [attachmentIds, setAttachmentIds] = useState<number[]>([]);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [failedUploadModalOpen, setFailedUploadModalOpen] =
    useState<boolean>(false);
  const [failedUploads, setFailedUploads] = useState<string[]>([]);

  const [createNewAttachment] = useCommentsApiV1CreateAttachmentMutation();

  const max_filesize_bytes = ATTACHMENT_MAX_SIZE_MB * BYTES_IN_MB;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newFiles: FileList | null = (event.target as HTMLInputElement).files;
    const newPromises: { promise: Promise<void>; fileName: string }[] = [];
    const allowedFileTypes = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/mov",
      "image/mp4",
    ];

    if (newFiles && newFiles.length > 0) {
      Array.from(newFiles).map((file: File) => {
        if (file) {
          if (!allowedFileTypes.includes(file.type)) {
            alert("Unsupported file type");
            return null;
          }

          if (file.size > max_filesize_bytes) {
            alert(
              `File too large. Please upload files less than ${ATTACHMENT_MAX_SIZE_MB}MB in size.`,
            );
            return null;
          }
          const newPromise = createAttachment(file).finally(() => {
            setStoredFiles((prevFiles) => {
              return [...prevFiles, file];
            });
            setImageUrls((prevUrls) => {
              return [...prevUrls, URL.createObjectURL(file)];
            });
            setPendingUploads((prevPending) => prevPending - 1);
          });
          newPromises.push({ promise: newPromise, fileName: file.name });
        }
      });
    }

    setPendingUploads(newPromises.length);

    await Promise.allSettled(newPromises.map((p) => p.promise)).then(
      (results) => {
        const failedUploads = results
          .map((result, index) => ({
            ...result,
            fileName: newPromises[index].fileName,
          }))
          .filter((result) => result.status === "rejected")
          .map((result) => result.fileName);

        setFailedUploads(failedUploads);

        if (failedUploads.length > 0) {
          setFailedUploadModalOpen(true);
        }
      },
    );
  };

  const createAttachment = async (file: File) => {
    if (!file) return;

    try {
      const res = await createNewAttachment({
        orderGroupId: orderGroupId,
        attachmentIn: {
          fileName: file.name,
        },
      });

      const payload = (res as unknown as { data: UploadPayload }).data;
      await uploadToS3({ payload, file });
      setAttachmentIds((prevIds) => {
        if (typeof payload.attachment_id === "number") {
          return [...prevIds, payload.attachment_id];
        } else {
          return prevIds;
        }
      });
    } catch {
      alert("Could not create attachment");
    }
  };

  const sendDisabled = !comment && storedFiles.length === 0;

  // Detect unsaved changes to TextField or storedFiles and warn user on page refresh or close
  // back button is usually not detectable
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (comment || storedFiles.length > 0) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [comment, storedFiles]);

  const clearInputs = () => {
    setComment("");
    setImageUrls([]);
    setStoredFiles([]);
    setAttachmentIds([]);
  };
  const deletePendingAttachment = (index: number) => {
    const newStoredFiles = [...storedFiles];
    newStoredFiles.splice(index, 1);
    setStoredFiles(newStoredFiles);

    const newImageUrls = [...imageUrls];
    newImageUrls.splice(index, 1);
    setImageUrls(newImageUrls);

    const newAttachmentIds = [...attachmentIds];
    newAttachmentIds.splice(index, 1);
    setAttachmentIds(newAttachmentIds);
  };

  const postComment = () => {
    sendComment(comment, attachmentIds);
    clearInputs();
  };

  if (isLoading) {
    return (
      <Stack
        data-testid="send-comment-skeleton"
        sx={{
          gap: 1,
        }}
      >
        <Skeleton variant="rounded" width="100%" height={56} />
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Skeleton variant="rounded" width="30%" height={37} />
          <Skeleton variant="rounded" width="30%" height={37} />
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        {/* Text Input */}
        <TextField
          variant="outlined"
          fullWidth
          multiline
          value={comment}
          label="Add a comment.."
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setComment(event.currentTarget.value);
          }}
          data-testid="comment-input-text"
        />
        {/* Pending Uploads */}
        {pendingUploads > 0 && (
          <Grid container spacing={0.5}>
            {Array.from({ length: pendingUploads }, (_, index) => (
              <Grid key={`image-url-${index}`} size={6}>
                <Stack
                  key={index}
                  sx={{
                    bgcolor: "grey.500",
                    height: 240,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <PhotoIcon
                    sx={{
                      position: "absolute",
                      width: 64,
                      height: 64,
                      color: "grey.800",
                    }}
                  />
                  <CircularProgress sx={{ color: "white" }} />
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Attachment Previews */}
        {imageUrls.length > 0 && (
          <>
            <Typography variant="subtitle2">
              Attachments ({imageUrls.length})
            </Typography>
            <Grid container spacing={0.5}>
              {storedFiles.map((file, index) => {
                return (
                  <Grid key={`image-url-${index}`} size={{ xs: 6 }}>
                    <ImageBox
                      fileName={file.name}
                      url={imageUrls[index]}
                      onDelete={() => deletePendingAttachment(index)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* Buttons */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="text"
            startIcon={<AttachFileIcon />}
            onClick={() => inputFile?.current?.click()}
          >
            Add Attachments
            <input
              type="file"
              data-testid="file-input"
              ref={inputFile}
              onChange={handleFileChange}
              accept="image/*"
              hidden
              multiple={true}
            />
          </Button>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <Button
              disabled={sendDisabled}
              onClick={clearInputs}
              variant="outlined"
              data-testid="cancel-comment-button"
              fullWidth
            >
              Cancel
            </Button>
            <Button
              disabled={sendDisabled}
              onClick={postComment}
              variant="contained"
              data-testid="send-comment-button"
              fullWidth
            >
              Post
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Dialog
        open={failedUploadModalOpen}
        onClose={() => setFailedUploadModalOpen(false)}
        data-testid="failed-upload-modal"
      >
        <DialogTitle sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ErrorIcon color="warning" />
          Unable to attach
        </DialogTitle>
        <DialogContent>
          An error occurred while uploading the following files:
          <ul>
            {failedUploads.map((fileName, index) => (
              <li key={`failed-upload-${index}`}>{fileName}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            color="primary"
            onClick={() => setFailedUploadModalOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SendComment;
